/**
 * Shuttle Payment Failed Page Manager
 * Handles dynamic content population on the payment failed page
 * 
 * @package A2A_Theme
 */

class ShuttlePaymentFailedManager {
    constructor() {
        this.bookingData = null;
        this.orderData = null;
        this.checkoutData = null;
    }
    
    /**
     * Initialize payment failed page
     */
    init() {
        // Load data from localStorage
        this.bookingData = this.getBookingData();
        this.orderData = this.getOrderData();
        this.checkoutData = this.getCheckoutData();
        
        if (!this.bookingData) {
            return;
        }
        
        // Update page content
        this.updateBookingSummary();
    }
    
    /**
     * Get booking data from localStorage
     */
    getBookingData() {
        try {
            const data = localStorage.getItem('shuttle_booking_data');
            return data ? JSON.parse(data) : null;
        } catch (e) {
            return null;
        }
    }
    
    /**
     * Get order data from localStorage
     */
    getOrderData() {
        try {
            const data = localStorage.getItem('shuttle_order_data');
            return data ? JSON.parse(data) : null;
        } catch (e) {
            return null;
        }
    }
    
    /**
     * Get checkout data from localStorage
     */
    getCheckoutData() {
        try {
            const data = localStorage.getItem('shuttle_checkout_data');
            return data ? JSON.parse(data) : null;
        } catch (e) {
            return null;
        }
    }
    
    /**
     * Update booking summary section
     */
    updateBookingSummary() {
        // Update route information
        this.updateSummaryRouteInfo();
        
        // Update line items (transfers, extras)
        this.updateSummaryLineItems();
        
        // Update total
        this.updateSummaryTotal();
    }
    
    /**
     * Update summary route information
     */
    updateSummaryRouteInfo() {
        if (!this.bookingData) return;
        
        const isReturn = this.bookingData.transferType === 'return';
        
        // Update outbound section
        this.updateTripSection('.shuttle-payment-failed__section:first-of-type', 'outbound');
        
        // Update return section
        if (isReturn) {
            const returnSection = document.querySelectorAll('.shuttle-payment-failed__section')[1];
            if (returnSection) {
                this.updateTripSection('.shuttle-payment-failed__section:nth-of-type(2)', 'return');
            } else {
                // Return section doesn't exist, hide divider
                const divider = document.querySelector('.shuttle-payment-failed__divider');
                if (divider) divider.style.display = 'none';
            }
        } else {
            // Hide return section for one-way
            const returnSection = document.querySelectorAll('.shuttle-payment-failed__section')[1];
            if (returnSection) {
                returnSection.style.display = 'none';
            }
            const divider = document.querySelector('.shuttle-payment-failed__divider');
            if (divider) divider.style.display = 'none';
        }
    }
    
    /**
     * Update individual trip section
     */
    updateTripSection(selector, direction) {
        const section = document.querySelector(selector);
        if (!section) return;
        
        const route = this.bookingData.selectedRoutes[direction];
        if (!route) return;
        
        const from = direction === 'outbound' ? this.bookingData.from : this.bookingData.to;
        const to = direction === 'outbound' ? this.bookingData.to : this.bookingData.from;
        const date = direction === 'outbound' ? this.bookingData.outboundDate : this.bookingData.returnDate;
        const time = route.departureTime;
        
        // Format date with departure time
        const formattedDate = this.formatDateWithDepartureTime(date, time);
        
        // Get passenger count
        const adults = this.bookingData.passengers?.adults || 0;
        const children = this.bookingData.passengers?.children || 0;
        let passengerText = '';
        if (adults > 0) passengerText += `${adults} adult${adults > 1 ? 's' : ''}`;
        if (children > 0) passengerText += (passengerText ? ', ' : '') + `${children} kid${children > 1 ? 's' : ''}`;
        
        // Get price
        const price = this.getTransferPrice(direction);
        
        // Update route text
        const routeSpan = section.querySelector('.shuttle-payment-failed__trip-route p:first-child span:first-child');
        if (routeSpan) {
            routeSpan.textContent = `${from.label} → ${to.label}`;
        }
        
        // Update passenger and date text
        const detailsP = section.querySelector('.shuttle-payment-failed__trip-route p:last-child');
        if (detailsP) {
            detailsP.textContent = `${passengerText}. ${formattedDate}.`;
        }
        
        // Update prices (both mobile and desktop)
        const priceAmounts = section.querySelectorAll('.shuttle-payment-failed__price-amount');
        priceAmounts.forEach(el => {
            el.textContent = price;
        });
    }
    
    /**
     * Format date with departure time
     */
    formatDateWithDepartureTime(dateStr, time) {
        try {
            // dateStr format: "09.10.25, 04:45"
            // time format: "05:50"
            
            const dateParts = dateStr.split(',')[0].split('.');
            const day = dateParts[0];
            const month = dateParts[1];
            const year = `20${dateParts[2]}`;
            
            const date = new Date(`${year}-${month}-${day}`);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
            const monthName = date.toLocaleDateString('en-US', { month: 'short' });
            const dayNum = date.getDate();
            
            return `${dayName}, ${dayNum} ${monthName} ${year}, ${time}`;
        } catch (e) {
            return dateStr;
        }
    }
    
    /**
     * Get transfer price for a direction
     */
    getTransferPrice(direction) {
        if (!this.orderData?.calculatedTotals) {
            return '€0.00';
        }
        
        const totals = this.orderData.calculatedTotals[direction];
        if (!totals) return '€0.00';
        
        const currency = totals.currency || 'EUR';
        const total = totals.totalWithExtras || 0;
        
        return this.formatPrice(total, currency);
    }
    
    /**
     * Update summary line items
     */
    updateSummaryLineItems() {
        if (!this.orderData?.calculatedTotals) return;
        
        const summaryDiv = document.querySelector('.shuttle-payment-failed__summary');
        if (!summaryDiv) return;
        
        // Clear existing rows except total
        const rows = summaryDiv.querySelectorAll('.shuttle-payment-failed__summary-row');
        rows.forEach(row => row.remove());
        
        const totalRow = summaryDiv.querySelector('.shuttle-payment-failed__summary-total');
        
        // Add outbound transfer
        if (this.orderData.calculatedTotals.outbound) {
            const price = this.getTransferPrice('outbound');
            const row = this.createSummaryRow('Outbound Transfer', price);
            summaryDiv.insertBefore(row, totalRow);
        }
        
        // Add return transfer
        if (this.orderData.calculatedTotals.return) {
            const price = this.getTransferPrice('return');
            const row = this.createSummaryRow('Return Transfer', price);
            summaryDiv.insertBefore(row, totalRow);
        }
    }
    
    /**
     * Create summary row element
     */
    createSummaryRow(label, value) {
        const row = document.createElement('div');
        row.className = 'shuttle-payment-failed__summary-row';
        row.innerHTML = `
            <span class="shuttle-payment-failed__summary-label">${label}</span>
            <span class="shuttle-payment-failed__summary-value">${value}</span>
        `;
        return row;
    }
    
    /**
     * Update summary total
     */
    updateSummaryTotal() {
        if (!this.orderData?.calculatedTotals) return;
        
        const totalValueEl = document.querySelector('.shuttle-payment-failed__total-value');
        if (!totalValueEl) return;
        
        // Calculate total from both directions
        let total = 0;
        let currency = 'EUR';
        
        if (this.orderData.calculatedTotals.outbound) {
            total += this.orderData.calculatedTotals.outbound.totalWithExtras || 0;
            currency = this.orderData.calculatedTotals.outbound.currency || 'EUR';
        }
        
        if (this.orderData.calculatedTotals.return) {
            total += this.orderData.calculatedTotals.return.totalWithExtras || 0;
            currency = this.orderData.calculatedTotals.return.currency || 'EUR';
        }
        
        totalValueEl.textContent = this.formatPrice(total, currency);
    }
    
    /**
     * Format price with currency
     */
    formatPrice(amount, currency) {
        // Get currency from orderData if available, otherwise use the passed currency
        if (this.orderData?.calculatedTotals?.outbound?.currency) {
            currency = this.orderData.calculatedTotals.outbound.currency;
        } else if (this.orderData?.calculatedTotals?.return?.currency) {
            currency = this.orderData.calculatedTotals.return.currency;
        }
        
        const rounded = Math.round(amount * 100) / 100;
        
        switch (currency) {
            case 'EUR':
                return `€${rounded.toFixed(2)}`;
            case 'GBP':
                return `£${rounded.toFixed(2)}`;
            case 'USD':
                return `$${rounded.toFixed(2)}`;
            case 'CHF':
                return `CHF ${rounded.toFixed(2)}`;
            case 'PLN':
                return `${rounded.toFixed(2)} zł`;
            case 'CZK':
                return `${rounded.toFixed(0)} Kč`;
            default:
                return `${currency} ${rounded.toFixed(2)}`;
        }
    }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
    const manager = new ShuttlePaymentFailedManager();
    manager.init();
});

